// Timeline column component for displaying tasks in a single operator column
// CREATED: Extracted from DayTimeline.tsx for reusability
// UPDATED: Uses TimelineContext to eliminate prop drilling (24 props -> 5)
// Combines TimelineGrid with task blocks and drag & drop event handling

import { useRef, useCallback, useMemo } from 'react';
import { TimelineGrid, TIMELINE_HEIGHT } from './TimelineGrid';
import { TimelineTaskBlock } from './TimelineTaskBlock';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { useTimelineContext } from './TimelineContext';
import type { TimelineTask } from '../../../hooks/useDragDropTimeline';
import { timeToMinutes, doTimeSlotsOverlap } from '../../../lib/timeCalculations';

interface TaskLayout {
  task: TimelineTask;
  column: number;
  totalColumns: number;
}

interface TimelineColumnProps {
  operatorId: string;
  tasks: TimelineTask[];
  columnWidth?: number;
  gridLineColor?: string;
  showCurrentTime?: boolean;
}

function calculateTaskLayouts(
  tasks: TimelineTask[],
  overlap: (t1: TimelineTask, t2: TimelineTask) => boolean
): TaskLayout[] {
  const sorted = [...tasks].sort((a, b) => {
    const startDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    if (startDiff !== 0) return startDiff;
    return b.duration - a.duration;
  });

  const layouts: TaskLayout[] = [];

  sorted.forEach((task, index) => {
    const overlappingTasks = sorted.filter((otherTask, otherIndex) => {
      if (otherIndex === index) return false;
      return overlap(task, otherTask);
    });

    if (overlappingTasks.length === 0) {
      layouts.push({ task, column: 0, totalColumns: 1 });
    } else {
      const assignedOverlapping = layouts.filter(layout =>
        overlappingTasks.some(ot => ot.id === layout.task.id)
      );

      const usedColumns = new Set(assignedOverlapping.map(l => l.column));

      let column = 0;
      while (usedColumns.has(column)) {
        column++;
      }

      const maxColumn = Math.max(column, ...assignedOverlapping.map(l => l.column));
      const totalColumns = maxColumn + 1;

      assignedOverlapping.forEach(layout => {
        layout.totalColumns = Math.max(layout.totalColumns, totalColumns);
      });

      layouts.push({ task, column, totalColumns });
    }
  });

  return layouts;
}

function tasksOverlap(task1: TimelineTask, task2: TimelineTask): boolean {
  return doTimeSlotsOverlap(
    { startTime: task1.startTime, duration: task1.duration },
    { startTime: task2.startTime, duration: task2.duration }
  );
}

export function TimelineColumn({
  operatorId,
  tasks,
  columnWidth,
  gridLineColor = 'bg-gray-200',
  showCurrentTime = false
}: TimelineColumnProps) {
  const timelineRef = useRef<HTMLDivElement>(null);

  const {
    draggedTask,
    draggedTaskPosition,
    isTouchDragging,
    isMobile,
    onDragOver,
    onDragLeave,
    onDrop
  } = useTimelineContext();

  const layouts = useMemo(() => calculateTaskLayouts(tasks, tasksOverlap), [tasks]);

  const handleDragOverWrapper = useCallback((e: React.DragEvent) => {
    onDragOver(e, operatorId, timelineRef.current);
  }, [onDragOver, operatorId]);

  const handleDropWrapper = useCallback((e: React.DragEvent) => {
    onDrop(e, operatorId);
  }, [onDrop, operatorId]);

  const isDropTarget = draggedTaskPosition?.operatorId === operatorId;

  return (
    <div
      className={`flex-shrink-0 border-r border-gray-200 transition-colors ${
        isDropTarget ? 'bg-blue-50/30' : ''
      }`}
      style={{ width: columnWidth ? `${columnWidth}px` : undefined, touchAction: isTouchDragging ? 'none' : 'auto' }}
      onDragOver={handleDragOverWrapper}
      onDragLeave={onDragLeave}
      onDrop={handleDropWrapper}
    >
      <div ref={timelineRef} data-timeline-column className="relative" style={{ height: `${TIMELINE_HEIGHT}px` }}>
        <TimelineGrid gridKeyPrefix={`grid-${operatorId}`} lineColor={gridLineColor} />

        {showCurrentTime && <CurrentTimeIndicator showLabel={!isMobile} />}

        <div className="absolute inset-0 px-2">
          {layouts.map((layout) => {
            const { task, column, totalColumns } = layout;
            const isBeingDragged = draggedTask?.id === task.id;

            const displayOperatorId = isBeingDragged && draggedTaskPosition
              ? draggedTaskPosition.operatorId
              : task.operatorId;

            if (isBeingDragged && displayOperatorId !== operatorId) {
              return null;
            }

            if (!isBeingDragged && task.operatorId !== operatorId) {
              return null;
            }

            return (
              <TimelineTaskBlock
                key={task.id}
                task={task}
                column={column}
                totalColumns={totalColumns}
                operatorId={operatorId}
              />
            );
          })}

          {draggedTask && draggedTaskPosition && draggedTaskPosition.operatorId === operatorId && draggedTask.operatorId !== operatorId && (
            <TimelineTaskBlock
              key={`dragged-${draggedTask.id}`}
              task={draggedTask}
              column={0}
              totalColumns={1}
              operatorId={operatorId}
              isGhostPreview
            />
          )}
        </div>
      </div>
    </div>
  );
}
